/**
 * Promotion Progress Manager
 * Calculates progress metrics and updates HTML/CSS progress bar elements.
 */
class LcPromotionProgress {
  /**
   * Calculate progress metrics for a promotion based on the cart.
   * @param {object} cart - Shopify Cart object
   * @param {object} promo - Promotion metadata object
   * @returns {object} Progress stats
   */
  static calculate(cart, promo) {
    let percentage = 0;
    let message = '';
    let isQualified = false;
    let current = 0;
    let target = 0;

    switch (promo.type) {
      case 'qty_percent':
      case 'qty_fixed':
      case 'buy_x_get_y':
        current = LcPromotionUtils.getQualifyingQuantity(cart, promo);
        target = promo.requiredQuantity || 0;
        isQualified = current >= target;
        percentage = target > 0 ? Math.min(100, (current / target) * 100) : 100;
        
        const qtyDiff = Math.max(0, target - current);
        if (isQualified) {
          message = `Offer unlocked! ${promo.giftProduct ? 'Free gift added.' : 'Discount applied.'}`;
        } else {
          message = `Buy <strong>${target}</strong>, you have <strong>${current}</strong>. Add <strong>${qtyDiff}</strong> more to unlock!`;
        }
        break;

      case 'spend_gift':
      case 'spend_shipping':
        current = LcPromotionUtils.getQualifyingSubtotal(cart, promo);
        target = (promo.minimumCartValue || 0) * 100; // convert to cents
        isQualified = current >= target;
        percentage = target > 0 ? Math.min(100, (current / target) * 100) : 100;

        const spendDiff = Math.max(0, target - current);
        if (isQualified) {
          message = `Offer unlocked! ${promo.type === 'spend_shipping' ? 'Free shipping applied.' : 'Free gift added.'}`;
        } else {
          const currentMoney = LcPromotionUtils.formatMoney(current);
          const diffMoney = LcPromotionUtils.formatMoney(spendDiff);
          message = `Spend <strong>${LcPromotionUtils.formatMoney(target)}</strong>. Current: <strong>${currentMoney}</strong>. Need <strong>${diffMoney}</strong> more.`;
        }
        break;

      case 'bundle':
        // Bundle is qualified if ALL target products are present in the cart
        const bundleProducts = promo.targetProducts || [];
        if (bundleProducts.length === 0) {
          isQualified = true;
          percentage = 100;
          message = 'Bundle offer active!';
        } else {
          let presentCount = 0;
          const missingNames = [];
          
          bundleProducts.forEach(handle => {
            const inCart = cart.items.some(item => item.handle === handle);
            if (inCart) {
              presentCount++;
            } else {
              // Find matching title or use fallback name
              missingNames.push(handle.replace(/-/g, ' '));
            }
          });

          target = bundleProducts.length;
          current = presentCount;
          isQualified = current === target;
          percentage = target > 0 ? Math.min(100, (current / target) * 100) : 100;

          if (isQualified) {
            message = 'Bundle completed! Extra discount applied at checkout.';
          } else {
            message = `Add <strong>${missingNames.join(' & ')}</strong> to unlock bundle savings!`;
          }
        }
        break;

      case 'mix_match':
        // Mix & match: Buy any quantity of items from a collection
        current = LcPromotionUtils.getQualifyingQuantity(cart, promo);
        target = promo.requiredQuantity || 0;
        isQualified = current >= target;
        percentage = target > 0 ? Math.min(100, (current / target) * 100) : 100;
        
        const mixDiff = Math.max(0, target - current);
        if (isQualified) {
          message = 'Mix & Match offer unlocked!';
        } else {
          const colName = promo.targetCollection ? promo.targetCollection.replace(/-/g, ' ') : 'eligible';
          message = `Add <strong>${mixDiff}</strong> more <strong>${colName}</strong> items to save!`;
        }
        break;

      default:
        // Default fallback (e.g. flash sales)
        isQualified = true;
        percentage = 100;
        message = promo.title;
    }

    return { percentage, message, isQualified, current, target };
  }

  /**
   * Updates all progress UI elements (progress bars, message texts) on the screen.
   * @param {HTMLElement} container - The wrapper element containing widgets
   * @param {object} stats - Progress calculation statistics
   */
  static updateWidgetDOM(container, stats) {
    // 1. Update progress bar fill width
    const fill = container.querySelector('[data-promo-progress-fill]');
    if (fill) {
      fill.style.width = `${stats.percentage}%`;
    }

    // 2. Update progress percentage label
    const label = container.querySelector('[data-promo-progress-percent]');
    if (label) {
      label.textContent = `${Math.round(stats.percentage)}%`;
    }

    // 3. Update dynamic text message
    const msgs = container.querySelectorAll('[data-promo-progress-message]');
    msgs.forEach(msg => {
      msg.innerHTML = stats.message;
    });

    // 4. Toggle locked/unlocked classes & update badge text
    container.classList.toggle('is-locked', !stats.isQualified);
    container.classList.toggle('is-unlocked', stats.isQualified);
    const statusEl = container.querySelector('.lc-promo-cart-widget__status');
    if (statusEl) {
      statusEl.textContent = stats.isQualified ? 'UNLOCKED' : 'LOCKED';
    }
  }
}

window.LcPromotionProgress = LcPromotionProgress;
